import React, { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { combineLatest, of } from 'rxjs'
import { filter, map, switchMap } from 'rxjs/operators'
import url from 'libraries/front-end-core/core/url'
import { RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { TAKEOVER_INTERNAL } from 'libraries/front-end-core/constants/urls'
import { deselectContent, observeSelected } from 'libraries/front-end-core/services/StateService'
import { shorten } from 'libraries/front-end-core/services/ShortenerService'
import useObservable from 'libraries/front-end-core/hooks/useObservable'
import { isValidResponse } from 'libraries/front-end-core/helpers/dataflow'
import { URL_PDF_LEG } from 'constants/urls'
import { LEGISLATION_REVIEW } from 'constants/controllers'
import { requestAct, requestFolders } from 'services/LegislationService'
import LegislationBreadCrumbsContainer from 'redux/containers/pages/legislation/LegislationBreadCrumbsContainer'
import { setLegislationFolders } from 'redux/actions'
import SelectedHeader from 'components/shared/SelectedHeader'

export default (props) => {
  const folders = useObservable(requestFolders(), RESPONSE_PENDING)
  const dispatch = useDispatch()
  const { act } = props
  // Memoized on act alone (not recreated every render) - SelectedHeader's
  // useBaseState re-subscribes whenever this Observable's identity changes,
  // and a fresh combineLatest/switchMap chain starts back at its initial
  // pending state each time. An unmemoized version here caused a churn of
  // several resubscribe cycles on mount before `short` ever left `null`,
  // which is fine locally (~180ms) but slow enough on BrowserStack's real
  // network to sometimes still be in progress when a test's Share click
  // lands - confirmed live via debug trace: if the click's setView('share')
  // fires before `short` first leaves null, and the churn is still ongoing
  // afterwards, SelectedHeader's own `[props]` effect (which resets view
  // back to 'default' whenever props changes identity, and props includes
  // this stateSource) fires again post-click and permanently reverts the
  // just-opened share panel, with nothing left to reopen it.
  const stateSource = useMemo(() => (
    act
      ? combineLatest(
        [requestAct({ act }).pipe(filter(isValidResponse), map(a => a.nodes[a.root].leaves)),
          observeSelected('legislation')],
        (leaves, selected) => leaves.filter(leaf => selected[leaf] > 0)
      ).pipe(
        switchMap(
          selectedIds =>
            selectedIds.length
              ? shorten(url(LEGISLATION_REVIEW, { selectedIds })
                .replace(/#/, ''))
                .pipe(map(short => ({ selectedIds, short })))
              : of({ selectedIds: [], short: null })
        )
      )
      : of({ selectedIds: [], short: null })
  ), [act])

  useEffect(() => {
    dispatch(
      setLegislationFolders(folders)
    )
  }, [folders])

  return (
    <div className='bg-page-background pv6'>
      <SelectedHeader
        {...props}
        stateSource={stateSource}
        deselectAll={deselectContent.bind(null, 'legislation')}
        keyword={(l) => l === 1 ? 'section' : 'sections'}
        pdfUrl={URL_PDF_LEG}
        dataHelpItem='legislationtoolbar'
        displayPdf={act !== TAKEOVER_INTERNAL}
        displayPrint={act !== TAKEOVER_INTERNAL}
        displayWordDownload={false}
      />
      <LegislationBreadCrumbsContainer />
    </div>
  )
}
